import type { ReactNode } from "react";
import { CheckCircle2, Info } from "lucide-react";
import Banner, { type BannerVariant } from "@cle/components/Banner";
import { formatLessons } from "@cle/utils/lessonLimits";

export type ReportStatusBannerProps = {
  id?: string;
  reportedLessons: number;
  requiredLessons: number;
  transferredLessons: number;
};

const getRemainingLessons = ({
  reportedLessons,
  requiredLessons,
  transferredLessons,
}: Pick<
  ReportStatusBannerProps,
  "reportedLessons" | "requiredLessons" | "transferredLessons"
>): number =>
  Math.max(requiredLessons - transferredLessons - reportedLessons, 0);

type ReportStatusBannerConfig = {
  icon: ReactNode;
  message: ReactNode;
  variant: BannerVariant;
};

const getReportStatusBannerConfig = (
  props: ReportStatusBannerProps,
): ReportStatusBannerConfig | null => {
  const remaining = getRemainingLessons(props);

  if (remaining <= 0) {
    return {
      icon: <CheckCircle2 size={17} strokeWidth={2} />,
      message: (
        <p className="mb-0">
          Du har opfyldt kravet for lektioner - klar til at indberette!
        </p>
      ),
      variant: "success",
    };
  }

  return {
    icon: <Info size={17} strokeWidth={2} />,
    message: (
      <p className="mb-0">
        Du mangler <span className="fw-bold">{formatLessons(remaining)}</span>{" "}
        lektioner for at opfylde kravet.
      </p>
    ),
    variant: "danger",
  };
};

const ReportStatusBanner = ({
  id,
  reportedLessons,
  requiredLessons,
  transferredLessons,
}: ReportStatusBannerProps) => {
  const config = getReportStatusBannerConfig({
    reportedLessons,
    requiredLessons,
    transferredLessons,
  });

  if (!config) {
    return null;
  }

  return (
    <Banner id={id} icon={config.icon} role="status" variant={config.variant}>
      {config.message}
    </Banner>
  );
};

export default ReportStatusBanner;
